import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Stack
} from '@mui/material';
import { useGarage } from '../../contexts/GarageContext';
import { apiService } from '../../services/api';
import { getLocalDateString } from '../../utils/dateUtils';

const ReportsTab = () => {
  const { selectedGarage } = useGarage();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: getLocalDateString(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)),
    end: getLocalDateString()
  });

  useEffect(() => {
    if (selectedGarage?.id) {
      fetchReportData();
    }
  }, [selectedGarage?.id, dateRange]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      const response = await apiService.getGarageBookings(selectedGarage.id, {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      const bookings = response.bookings || [];
      
      // Calculate statistics
      const stats = {
        total: bookings.length,
        completed: bookings.filter(b => b.status === 'completed').length,
        passed: bookings.filter(b => b.mot_result === 'pass').length,
        failed: bookings.filter(b => b.mot_result === 'fail').length,
        cancelled: bookings.filter(b => b.status === 'cancelled').length,
        revenue: bookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + parseFloat(b.total_amount || 0), 0)
      };
      
      setReportData({ bookings, stats });
    } catch (err) {
      console.error('Failed to fetch report data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Date Range Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Report Period
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              onClick={fetchReportData}
              fullWidth
              sx={{ height: '56px' }}
            >
              Generate Report
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {reportData && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 3, mb: 3 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {reportData.stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Bookings
              </Typography>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {reportData.stats.completed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                {reportData.stats.completed > 0 ? Math.round((reportData.stats.passed / reportData.stats.completed) * 100) : 0}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pass Rate
              </Typography>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" fontWeight="bold">
                £{reportData.stats.revenue.toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Revenue
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Detailed Bookings Table */}
      {reportData && reportData.bookings.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Booking Details ({reportData.bookings.length} bookings)
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Products & Services</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Payment Status</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.bookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {new Date(booking.booking_date).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.booking_time}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {booking.user?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.user?.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="500">
                            {booking.vehicle?.registration_number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {booking.vehicle?.make} {booking.vehicle?.model}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {booking.products && booking.products.length > 0 ? (
                            <Stack spacing={0.5}>
                              {booking.products.map((productName, index) => (
                                <Typography key={index} variant="body2" fontSize="0.8rem">
                                  {productName}
                                </Typography>
                              ))}
                              {booking.addons && booking.addons.length > 0 && (
                                booking.addons.map((addonName, index) => (
                                  <Typography key={index} variant="caption" color="text.secondary">
                                    + {addonName}
                                  </Typography>
                                ))
                              )}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No services selected
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            £{booking.total_amount}
                          </Typography>
                          {booking.discount_amount > 0 && (
                            <Typography variant="caption" color="success.main">
                              (-£{booking.discount_amount})
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={booking.payment_status || 'pending'}
                          color={
                            booking.payment_status === 'paid' ? 'success' :
                            booking.payment_status === 'refunded' ? 'warning' :
                            booking.payment_status === 'failed' ? 'error' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={booking.status}
                          color={
                            booking.status === 'completed' ? 'success' :
                            booking.status === 'in_progress' ? 'warning' :
                            booking.status === 'confirmed' ? 'primary' :
                            booking.status === 'cancelled' ? 'error' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ReportsTab;